import {
  EVENT_BEGIN_CHECKOUT,
  EVENT_PAGE_VIEW,
  EVENT_PURCHASE,
} from "../constants"
import type { AnalyticsEvent, AnalyticsItem } from "../events"

/**
 * Story 10.10 — adaptador do ÚNICO vendor que a produção de facto usa: Google Tag (gtag.js).
 *
 * Existe porque `gtag.js` NÃO lê objetos crus do `window.dataLayer`: só regista o que chega
 * pelo shim `gtag(...)`, que empurra um objeto `arguments`. O contrato neutro da casa
 * (`AnalyticsEvent`) mantém-se puro e testável; a tradução para GA4 acontece aqui, na
 * fronteira, e é a única parte do módulo que conhece o vendor.
 */

// Shim global instalado pelo bootstrap do gtag (e por nós, antes do primeiro evento).
// A assinatura é variádica por natureza: `gtag("js", Date)`, `gtag("config", id, params)`,
// `gtag("event", name, params)`.
type GtagCommandFunction = (...args: readonly unknown[]) => void

declare global {
  interface Window {
    gtag?: GtagCommandFunction
  }
}

/** Sink do vendor. Tipado aqui (e não importado do pipeline) para não criar ciclo de import. */
export type GtagSink = (event: AnalyticsEvent) => void

/**
 * Prefixos de id aceites: GA4 (`G-`) e Google Tag (`GT-`).
 *
 * `AW-` (Google Ads) foi REMOVIDO de propósito: uma conversão de Ads só é registada com
 * `send_to`, que os eventos da casa não emitem — aceitá-lo carregaria um tag que não mede
 * conversão nenhuma, o mesmo argumento com que a story recusa o pixel Meta. A produção,
 * aliás, tem `adsConversionID` vazio e `adsLinked: false`.
 */
export const GTAG_ID_PREFIXES = ["G-", "GT-"] as const

/** Origem do loader do vendor. O id entra como query param (ver `buildGtagScriptSrc`). */
export const GTAG_SCRIPT_SRC = "https://www.googletagmanager.com/gtag/js"

/** `id` estável do `<Script>` (o `next/script` dedupa por ele entre navegações). */
export const GTAG_SCRIPT_ELEMENT_ID = "ve-gtag"

/**
 * Prefixo dos cookies que o `gtag.js` grava no browser: `_ga` (id de cliente) e `_ga_<id>`
 * (estado da sessão), ambos com ~2 anos de validade. Apagá-los é o que torna a retirada de
 * consentimento real — ver `clearVendorCookies` em `../consent`.
 */
export const GTAG_COOKIE_NAME_PREFIX = "_ga"

// Corpo do id depois do prefixo: só maiúsculas e dígitos. É uma ALLOWLIST DE FORMATO, não
// uma validação de existência — serve para nenhuma env malformada conseguir montar um `src`
// arbitrário (fail-closed silencioso).
const GTAG_ID_BODY_PATTERN = /^[A-Z0-9]+$/

const GTAG_COMMAND_EVENT = "event"
const GTAG_COMMAND_CONFIG = "config"
const GTAG_COMMAND_JS = "js"

const URL_QUERY_SEPARATOR = "?"
const URL_HASH_SEPARATOR = "#"
const QUERY_PAIR_SEPARATOR = "&"
const QUERY_VALUE_SEPARATOR = "="

/**
 * Parâmetros de query PRESERVADOS no `page_location`/`page_referrer` (allowlist).
 *
 * Existe porque um corte cego de toda a query — a primeira forma deste saneamento — apagava
 * também a ATRIBUIÇÃO: o GA4 deriva `source/medium/campaign` dos `utm_*`/`gclid`/… presentes
 * no URL do hit, logo 100% do tráfego pago entraria como `(direct)/(none)` e a story falharia
 * precisamente no que existe para medir. Tudo o resto continua a cair (em especial o `?t=` da
 * confirmação de pedido, um token que autoriza LER o pedido e RECLAMAR a conta).
 *
 * É ALLOWLIST e não denylist de propósito: um parâmetro novo e desconhecido é sempre
 * descartado — nenhum dado que ninguém pensou em avaliar viaja para o vendor por omissão.
 * `ref` é o código do afiliado (Story 7.1): identifica um canal, nunca uma pessoa.
 */
export const ALLOWED_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "gad_source",
  "srsltid",
  "msclkid",
  "fbclid",
  "ref",
] as const

const ALLOWED_QUERY_PARAM_SET: ReadonlySet<string> = new Set(ALLOWED_QUERY_PARAMS)

/** Item no formato GA4 — só id de variante e quantidade, nunca PII. */
export type GtagItem = {
  item_id: string
  quantity?: number
}

/**
 * Parâmetros GA4 canónicos que os três eventos da casa produzem.
 *
 * `page_location`/`page_referrer` NÃO são decoração: o `gtag.js` anexa-os sozinho a todos os
 * comandos, com o href COMPLETO. Declará-los explicitamente é o que sobrepõe essa auto-coleta
 * pela versão saneada (ver `sanitizeUrl`).
 */
export type GtagEventParams = {
  page_location?: string
  page_referrer?: string
  transaction_id?: string
  currency?: string
  value?: number
  items?: GtagItem[]
}

export type GtagEvent = {
  name: string
  params: GtagEventParams
}

/** Contexto da página, JÁ SANEADO (ver `sanitizeUrl`). Referrer vazio ⇒ parâmetro omitido. */
export type GtagPageContext = {
  location: string
  referrer: string
}

export type GtagConfigParams = {
  send_page_view: boolean
  page_location: string
  page_referrer?: string
}

/**
 * Devolve o URL sem `#hash` e com a query reduzida à `ALLOWED_QUERY_PARAMS`. Nunca lança:
 * entrada vazia ou ilegível ⇒ "".
 *
 * PII (a razão de existir): o `gtag.js` anexa `page_location` (href COMPLETO, com query) e
 * `page_referrer` a TODOS os eventos. A confirmação de pedido é
 * `/order/{id}/confirmed?t=<token>` — e esse token autoriza LER o pedido e RECLAMAR a conta.
 * Deixá-lo viajar para o Google violaria o "Never" da story; por isso tudo o que sai daqui
 * passa por esta redação. O que a allowlist mantém — e porquê — está em `ALLOWED_QUERY_PARAMS`.
 *
 * Sem `decodeURIComponent` (nem `URLSearchParams`, que decodifica): um `%` malformado lançaria
 * `URIError` numa função que corre no efeito do root layout. Um nome percent-encoded não casa a
 * allowlist e é descartado — fail-closed, que é o lado certo para falhar.
 */
export function sanitizeUrl(url: string): string {
  if (!url) {
    return ""
  }
  const trimmed = url.trim()
  const hashIndex = trimmed.indexOf(URL_HASH_SEPARATOR)
  const withoutHash = hashIndex === -1 ? trimmed : trimmed.slice(0, hashIndex)
  const queryIndex = withoutHash.indexOf(URL_QUERY_SEPARATOR)
  if (queryIndex === -1) {
    return withoutHash
  }
  const kept = keepAllowedQueryParams(withoutHash.slice(queryIndex + 1))
  const base = withoutHash.slice(0, queryIndex)
  return kept ? `${base}${URL_QUERY_SEPARATOR}${kept}` : base
}

function keepAllowedQueryParams(query: string): string {
  const kept: string[] = []
  for (const pair of query.split(QUERY_PAIR_SEPARATOR)) {
    if (!pair) {
      continue
    }
    const separatorIndex = pair.indexOf(QUERY_VALUE_SEPARATOR)
    const name = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex)
    if (ALLOWED_QUERY_PARAM_SET.has(name.toLowerCase())) {
      kept.push(pair)
    }
  }
  return kept.join(QUERY_PAIR_SEPARATOR)
}

/**
 * `page_location` de um `page_view`: o path do evento resolvido contra a página atual (que já
 * chega saneada). Sem base utilizável (SSR, location vazio) devolve o path saneado — o mais
 * honesto que existe sem inventar uma origem.
 *
 * A query da base é HERDADA quando o path do evento não traz query e aponta para a mesma
 * página: o `usePathname` do loader devolve o pathname SEM query, logo resolvê-lo contra a base
 * descartaria os `utm_*`/`gclid` da landing — o `page_view` de entrada é justamente o hit de que
 * o GA4 tira a atribuição da sessão.
 */
export function resolvePageLocation(path: string, currentLocation: string): string {
  try {
    const resolved = new URL(path, currentLocation)
    if (!resolved.search) {
      const base = new URL(currentLocation)
      if (base.pathname === resolved.pathname) {
        resolved.search = base.search
      }
    }
    return sanitizeUrl(resolved.href)
  } catch {
    // `new URL` lança com base vazia/inválida; não é erro engolido, é o caso previsto de
    // não haver origem conhecida (SSR/teste) — o path saneado é a melhor aproximação.
    return sanitizeUrl(path)
  }
}

/**
 * Prefixo MAIS LONGO da lista que casa com o candidato.
 *
 * Não é detalhe: com um simples `find`, uma lista onde um prefixo é prefixo do outro
 * (`G-` antes de `GT-`) faria `GT-TWMX4NVZ` ser lido como corpo `T-TWMX4NVZ` e ser rejeitado.
 * A correção deixa de depender da ORDEM do array.
 */
export function findIdPrefix(
  candidate: string,
  prefixes: readonly string[]
): string | undefined {
  let longest: string | undefined
  for (const prefix of prefixes) {
    if (!candidate.startsWith(prefix)) {
      continue
    }
    if (longest === undefined || prefix.length > longest.length) {
      longest = prefix
    }
  }
  return longest
}

/** Aceita apenas ids com prefixo conhecido e corpo alfanumérico maiúsculo. */
export function isGtagId(candidate: string): boolean {
  const prefix = findIdPrefix(candidate, GTAG_ID_PREFIXES)
  if (!prefix) {
    return false
  }
  return GTAG_ID_BODY_PATTERN.test(candidate.slice(prefix.length))
}

export function buildGtagScriptSrc(id: string): string {
  return `${GTAG_SCRIPT_SRC}?id=${id}`
}

/**
 * Parâmetros do comando `config` — a parte PURA do bootstrap.
 *
 * `send_page_view: false` é OBRIGATÓRIO: o `config` emitiria um `page_view` sozinho e o loader
 * da casa já emite um por rota (e re-emite quando o consentimento muda) — duplicaria exatamente
 * a view de entrada que o dedup do loader existe para evitar.
 *
 * O `page_location`/`page_referrer` saneados também aqui: sem eles o PRÓPRIO `config` registaria
 * o href sujo (com o token do pedido).
 */
export function buildGtagConfigParams(page: GtagPageContext): GtagConfigParams {
  const params: GtagConfigParams = {
    send_page_view: false,
    page_location: page.location,
  }
  if (page.referrer) {
    params.page_referrer = page.referrer
  }
  return params
}

/**
 * A fila já tem o `config` deste id? Perguntar à FILA (em vez de guardar uma flag de módulo)
 * mantém o bootstrap idempotente entre remounts e continua correto se um terceiro reatribuir
 * `window.dataLayer` — o array novo precisa mesmo do `config` outra vez.
 */
export function hasGtagConfigCommand(
  dataLayer: readonly unknown[],
  vendorId: string
): boolean {
  return dataLayer.some((entry) => isConfigCommandFor(entry, vendorId))
}

/** Traduz a união neutra da casa para o par (nome, params) que o GA4 entende. */
export function toGtagEvent(
  event: AnalyticsEvent,
  page: GtagPageContext
): GtagEvent {
  if (event.name === EVENT_PAGE_VIEW) {
    return {
      name: EVENT_PAGE_VIEW,
      params: withPageContext(
        {},
        { ...page, location: resolvePageLocation(event.path, page.location) }
      ),
    }
  }
  if (event.name === EVENT_BEGIN_CHECKOUT) {
    const params: GtagEventParams = {
      currency: normalizeCurrency(event.currency),
      items: [{ item_id: event.itemId }],
    }
    if (event.value !== undefined) {
      params.value = event.value
    }
    return { name: EVENT_BEGIN_CHECKOUT, params: withPageContext(params, page) }
  }
  if (event.name === EVENT_PURCHASE) {
    return {
      name: EVENT_PURCHASE,
      params: withPageContext(
        {
          transaction_id: event.transactionId,
          currency: normalizeCurrency(event.currency),
          value: event.value,
          items: event.items.map(toGtagItem),
        },
        page
      ),
    }
  }
  // Guarda de exaustividade: um membro NOVO de `AnalyticsEvent` falha aqui em COMPILAÇÃO, em
  // vez de sair silenciosamente como `purchase` com `transaction_id`/`value` undefined.
  const exhaustive: never = event
  return exhaustive
}

/**
 * Sink do vendor, já ligado ao id: empurra o evento como COMANDO gtag (nunca como objeto cru,
 * que o `gtag.js` ignoraria), garantindo primeiro o bootstrap. SSR-safe (`typeof window`).
 */
export function createGtagSink(vendorId: string): GtagSink {
  return function pushGtagEvent(event: AnalyticsEvent): void {
    if (typeof window === "undefined") {
      return
    }
    const page = readPageContext()
    const push = ensureGtagShim(window)
    ensureGtagBootstrap(window, push, vendorId, page)
    const { name, params } = toGtagEvent(event, page)
    push(GTAG_COMMAND_EVENT, name, params)
  }
}

/**
 * Empurra `js` + `config` UMA vez, imediatamente antes do primeiro evento.
 *
 * ORDEM DOS COMANDOS (é onde a conversão se perdia): o GA4 DESCARTA o que chega à fila antes do
 * `config` do measurement id. Um `<Script>` inline de bootstrap corre depois dos efeitos do
 * React, logo o `page_view` — e sobretudo o `purchase` — entrava na fila primeiro: a conversão
 * desaparecia e ainda ficava marcada como medida. Por isso o bootstrap é empurrado por CÓDIGO,
 * daqui, e não por markup.
 */
function ensureGtagBootstrap(
  browserWindow: Window,
  push: GtagCommandFunction,
  vendorId: string,
  page: GtagPageContext
): void {
  if (hasGtagConfigCommand(readDataLayer(browserWindow), vendorId)) {
    return
  }
  push(GTAG_COMMAND_JS, new Date())
  push(GTAG_COMMAND_CONFIG, vendorId, buildGtagConfigParams(page))
}

/**
 * Devolve o shim `window.gtag`, instalando-o quando ainda não existe.
 *
 * O `gtag.js` carrega de forma assíncrona, logo os primeiros comandos são sempre nossos.
 * Instalar aqui o mesmo shim (push de `arguments` no dataLayer) garante que ficam em fila e são
 * reproduzidos quando o `gtag.js` arranca.
 */
function ensureGtagShim(browserWindow: Window): GtagCommandFunction {
  const existing = browserWindow.gtag
  if (typeof existing === "function") {
    return existing
  }
  const shim: GtagCommandFunction = function pushGtagCommand(): void {
    // `arguments` (e não os rest params) é intencional: o gtag.js só reconhece entradas do
    // dataLayer no formato do objeto `arguments` — é o shim oficial, palavra por palavra.
    // O array é lido AGORA e não capturado: se um terceiro reatribuir `window.dataLayer`, os
    // comandos ficariam num array órfão que ninguém lê.
    // eslint-disable-next-line prefer-rest-params
    readDataLayer(browserWindow).push(arguments)
  }
  browserWindow.gtag = shim
  return shim
}

function readDataLayer(browserWindow: Window): unknown[] {
  const existing = browserWindow.dataLayer
  if (Array.isArray(existing)) {
    return existing
  }
  const created: unknown[] = []
  browserWindow.dataLayer = created
  return created
}

// Casca impura: lê os globais do browser e devolve-os já saneados. Tolera globais ausentes ou
// de forma inesperada (ambiente de teste, browsers exóticos) sem lançar.
function readPageContext(): GtagPageContext {
  return {
    location: sanitizeUrl(readWindowHref()),
    referrer: sanitizeUrl(readDocumentReferrer()),
  }
}

function readWindowHref(): string {
  if (typeof window === "undefined") {
    return ""
  }
  const href = window.location?.href
  return typeof href === "string" ? href : ""
}

function readDocumentReferrer(): string {
  if (typeof document === "undefined") {
    return ""
  }
  const referrer = document.referrer
  return typeof referrer === "string" ? referrer : ""
}

// `page_location` vai SEMPRE (mesmo vazio): omiti-lo devolveria a coleta ao gtag.js, que
// anexaria o href sujo. `page_referrer` só quando existe — não há nada para sobrepor.
function withPageContext(
  params: GtagEventParams,
  page: GtagPageContext
): GtagEventParams {
  const enriched: GtagEventParams = { ...params, page_location: page.location }
  if (page.referrer) {
    enriched.page_referrer = page.referrer
  }
  return enriched
}

function isConfigCommandFor(entry: unknown, vendorId: string): boolean {
  if (typeof entry !== "object" || entry === null) {
    return false
  }
  const command = entry as ArrayLike<unknown>
  return command[0] === GTAG_COMMAND_CONFIG && command[1] === vendorId
}

function toGtagItem(item: AnalyticsItem): GtagItem {
  const mapped: GtagItem = { item_id: item.itemId }
  if (item.quantity !== undefined) {
    mapped.quantity = item.quantity
  }
  return mapped
}

// GA4 espera ISO-4217 em maiúsculas ("BRL"); o Medusa serve `currency_code` em minúsculas.
function normalizeCurrency(currency: string): string {
  return currency.toUpperCase()
}
